import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router'
import { Toaster } from 'sonner'
import { ApiError } from '@/api/client'
import { AuthProvider, useAuth } from '@/lib/auth'
import { I18nProvider } from '@/lib/i18n'
import { RealtimeProvider } from '@/lib/realtime'
import { ThemeProvider, useTheme } from '@/lib/theme'
import { reloadForUpdate } from '@/lib/updates'
import { router } from './router'
import './styles.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: (n, e) => !(e instanceof ApiError && [401, 403, 404, 405, 422].includes(e.status)) && n < 2,
    },
    mutations: { retry: false },
  },
})

function Live({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  return <RealtimeProvider enabled={!!user && user.role !== 'user'}>{children}</RealtimeProvider>
}

function ThemedToaster() {
  const { dark } = useTheme()
  return <Toaster position="top-right" richColors closeButton theme={dark ? 'dark' : 'light'} />
}

// Vite's own signal that a preloaded file of an old build is gone.
window.addEventListener('vite:preloadError', (event) => {
  if (reloadForUpdate()) event.preventDefault()
})

async function start() {
  // npm run dev:mock: a built-in mock of the API (never in production builds).
  if (import.meta.env.DEV && import.meta.env.VITE_MOCK === 'true') {
    const { startMocks } = await import('./mocks/browser')
    await startMocks()
  }
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ThemeProvider>
        <I18nProvider>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <Live>
                <RouterProvider router={router} />
                <ThemedToaster />
              </Live>
            </AuthProvider>
          </QueryClientProvider>
        </I18nProvider>
      </ThemeProvider>
    </StrictMode>,
  )
}

start()
